import React, { useEffect, useState } from 'react';
import { X, ArrowDownToLine, Loader2, Wallet, CheckCircle2, AlertTriangle, Copy, Check, ExternalLink, Sparkles } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { phantomDepositApi } from '../api';
import usePhantom from '../hooks/usePhantom';

const PhantomDepositModal = ({ open, onClose }) => {
  const { refresh } = useAuth();
  const phantom = usePhantom();
  const [info, setInfo] = useState(null);
  const [amount, setAmount] = useState('');
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    phantomDepositApi.info().then(setInfo).catch((e) => setError(e?.response?.data?.detail || e.message));
  }, [open]);

  if (!open) return null;

  const copy = (text) => {
    try { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };

  const openPhantomDeepLink = () => {
    if (!info?.treasury_wallet || !info?.mint) return;
    // Phantom send page (works in mobile Phantom app + desktop extension)
    const url = `https://phantom.app/ul/v1/transfer?spl_token=${info.mint}&recipient=${info.treasury_wallet}&amount=${encodeURIComponent(amount || 1)}`;
    window.open(url, '_blank');
  };

  const handleSubmit = async () => {
    setError(null);
    const amt = parseFloat(amount);
    const sig = (signature || '').trim();
    if (!(amt > 0)) { setError('Montant DOMIA invalide.'); return; }
    if (sig.length < 30) { setError('Signature de transaction Solana invalide.'); return; }
    setLoading(true);
    try {
      const res = await phantomDepositApi.confirm(sig, amt);
      setSuccess(res);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 sm:px-4 py-6" onClick={onClose} data-testid="phantom-deposit-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 bg-gradient-to-r from-blue-700 to-purple-700 text-white flex justify-between items-center">
          <div className="flex items-center gap-2">
            <ArrowDownToLine className="w-5 h-5" />
            <div>
              <div className="text-[11px] font-bold opacity-80 uppercase tracking-widest">Deposit via Phantom</div>
              <div className="text-lg font-black">Send DOMIA from your wallet</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full" data-testid="phantom-deposit-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {!success && (
            <>
              {!phantom.connected && (
                <button
                  onClick={async () => { try { await phantom.connect(); } catch (e) { setError(e.message); } }}
                  data-testid="phantom-deposit-connect-btn"
                  className="w-full flex items-center justify-center gap-2 rounded-xl py-3 font-extrabold text-[13px] bg-gradient-to-r from-purple-600 to-purple-800 text-white shadow-md hover:opacity-90"
                  disabled={phantom.connecting}
                >
                  <Wallet className="w-4 h-4" />
                  {phantom.available ? 'Connect Phantom' : 'Install Phantom'}
                </button>
              )}

              {phantom.connected && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-[12px] font-bold text-emerald-700 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Connected: <span className="font-mono">{phantom.publicKey.slice(0,6)}…{phantom.publicKey.slice(-4)}</span>
                </div>
              )}

              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-2">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Treasury wallet (DOMIA receiver)</div>
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2.5 py-2">
                  <code className="text-[11px] font-mono break-all flex-1">{info?.treasury_wallet || '…'}</code>
                  <button onClick={() => copy(info?.treasury_wallet)} className="p-1 rounded hover:bg-slate-100" data-testid="phantom-copy-treasury">
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-slate-500" />}
                  </button>
                </div>
                <div className="text-[10px] text-slate-500">Network: <strong>{info?.network || '…'}</strong> · Token: <strong>DOMIA</strong></div>
              </div>

              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 block mb-1.5">Amount DOMIA to send</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="100"
                  data-testid="phantom-deposit-amount-input"
                  className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-[15px] font-bold focus:outline-none focus:border-blue-500"
                />
                {phantom.connected && amount > 0 && (
                  <button
                    onClick={openPhantomDeepLink}
                    data-testid="phantom-deposit-open-link"
                    className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl py-2.5 font-extrabold text-[12px] bg-blue-600 text-white hover:bg-blue-700"
                  >
                    <ExternalLink className="w-4 h-4" /> Open in Phantom to send
                  </button>
                )}
              </div>

              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 block mb-1.5">Transaction signature (after sending)</label>
                <input
                  type="text"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Paste Solscan tx signature here…"
                  data-testid="phantom-deposit-signature-input"
                  className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-[11px] font-mono focus:outline-none focus:border-blue-500"
                />
                <p className="text-[10px] text-slate-500 mt-1">After sending DOMIA from Phantom, copy the transaction signature (Solscan) and paste it here. The site will verify on-chain and credit your account automatically.</p>
              </div>

              <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-3 py-2.5 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <span className="text-[11px] font-extrabold text-amber-800">🔥 Promo 24h : <strong>+$1.50 USD bonus DOMIA</strong> sur tous les dépôts effectués maintenant.</span>
              </div>

              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-[12px] font-bold text-red-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  {error}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading}
                data-testid="phantom-deposit-submit-btn"
                className="w-full rounded-xl py-3 font-black text-white text-[14px] tracking-widest uppercase shadow-lg disabled:opacity-50"
                style={{ background: 'linear-gradient(90deg, #1d4ed8, #7e22ce)' }}
              >
                {loading ? <Loader2 className="inline w-4 h-4 animate-spin mr-2" /> : null}
                {loading ? 'Verifying on-chain…' : 'Verify & Credit DOMIA'}
              </button>
            </>
          )}

          {success && (
            <div className="text-center space-y-3 py-2" data-testid="phantom-deposit-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
              <div className="text-xl font-black text-slate-900">Dépôt vérifié et crédité !</div>
              <div className="text-[13px] text-slate-600 space-y-1">
                <div>Reçu : <strong>{Number(success.amount).toFixed(2)} DOMIA</strong></div>
                {success.bonus > 0 && (
                  <div className="text-amber-700">Bonus 24h : <strong>+{Number(success.bonus).toFixed(0)} DOMIA</strong></div>
                )}
                <div className="text-emerald-700 font-extrabold">Total crédité : {Number(success.credited).toFixed(2)} DOMIA</div>
              </div>
              <button onClick={onClose} className="w-full rounded-xl py-2.5 font-extrabold text-white bg-blue-600">Fermer</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhantomDepositModal;
